import { AutorizedRole, SupportedLanguageSchema } from '@geartracker/types';

import { createZodDto } from './zod';

import { z } from 'zod';

const CreateMagicLinkSchema = z
  .object({
    email: z.email().trim().toLowerCase().nullable(),
    userId: z.number().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    organizationName: z.string().nullable(),
    language: SupportedLanguageSchema,
  })
  .refine(
    (data) => {
      // Either userId or email must be present
      return data.userId || (data.email && data.email.trim().length > 0);
    },
    {
      message: 'Either userId or email must be provided',
      path: ['email', 'userId'],
    },
  );

/**
 * Can be used to create a magic link for an existing user or a new user
 * - If userId is provided, email is not required
 * - If userId is not provided, email is required
 */
export class CreateMagicLinkDto extends createZodDto(CreateMagicLinkSchema) {}

const CreateEditSimpleUserSchema = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.email().trim().toLowerCase().nullable(),
    comment: z.string().nullable(),
    externalUserId: z.string().nullable(),
    role: z.enum(AutorizedRole),
    folderIds: z.array(z.number().int().positive()).optional().nullable(),
    tagIds: z.array(z.number().int().positive()).optional().nullable(),
    // Only applied on creation (see UsersService.createSimpleUser) — ignored
    // on update, editing a user's medical visit types goes through
    // SetUserMedicalVisitTypesDto instead.
    medicalVisitTypeIds: z
      .array(z.number().int().positive())
      .optional()
      .nullable(),
  })
  .refine(
    (data) => {
      // If role is not NONE, email must not be null or empty
      if (data.role !== AutorizedRole.NONE) {
        return data.email !== null && data.email.trim().length > 0;
      }
      // No requirement on email if role is NONE
      return true;
    },
    {
      message: 'Email is required for this role',
      path: ['email'],
    },
  );

export class CreateUpdateSimpleUserDto extends createZodDto(
  CreateEditSimpleUserSchema,
) {}

const UpdateCurrentUserSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  externalUserId: z.string().nullable(),
  shouldReceiveEmailPreference: z.boolean(),
});

const updateCurrentUserPasswordSchema = z.object({
  /*Account can have no password if only using SSO, so previous password is optional in that case*/
  oldPassword: z.string().trim().min(1).optional(),
  newPassword: z.string().min(12),
});

export class UpdateCurrentUserPasswordDto extends createZodDto(
  updateCurrentUserPasswordSchema,
) {}

//for personal user settings
export class UpdateCurrentUserDto extends createZodDto(
  UpdateCurrentUserSchema,
) {}

const UpdateLanguageSchema = z.object({
  language: z.string(),
});

export class UpdateLanguageDto extends createZodDto(UpdateLanguageSchema) {}

const InviteUserGroupSchema = z.object({
  userIds: z.array(z.number().int().positive()),
});

export class InviteUserGroupDto extends createZodDto(InviteUserGroupSchema) {}

const DeactivateUserGroupSchema = z.object({
  userIds: z.array(z.number().int().positive()),
});

/**
 * Bulk actions from the users list. `userIds` is always the raw selection: the
 * server decides who is eligible and reports the rest as skipped, rather than
 * trusting a client-side filter — the same contract as `invite-group`.
 */
const UpdateUserRoleGroupSchema = z.object({
  userIds: z.array(z.number().int().positive()).min(1),
  role: z.enum(AutorizedRole),
});

export class UpdateUserRoleGroupDto extends createZodDto(
  UpdateUserRoleGroupSchema,
) {}

export type UpdateUserRoleGroupInput = z.infer<
  typeof UpdateUserRoleGroupSchema
>;

const UserFolderAccessStateSchema = z.object({
  userIds: z.array(z.number().int().positive()).min(1),
});

export class UserFolderAccessStateDto extends createZodDto(
  UserFolderAccessStateSchema,
) {}

/**
 * Add/remove rather than replace: a bulk edit only knows the folders the
 * operator ticked or unticked. Sending a full desired list would silently
 * strip every folder they never saw — including those they have no access to.
 */
const UpdateUserFolderAccessGroupSchema = z
  .object({
    userIds: z.array(z.number().int().positive()).min(1),
    addFolderIds: z.array(z.number().int().positive()).default([]),
    removeFolderIds: z.array(z.number().int().positive()).default([]),
  })
  .refine(
    (data) => data.addFolderIds.length > 0 || data.removeFolderIds.length > 0,
    { message: 'Nothing to change', path: ['addFolderIds'] },
  )
  .refine(
    (data) =>
      !data.addFolderIds.some((id) => data.removeFolderIds.includes(id)),
    {
      message: 'A folder cannot be added and removed at once',
      path: ['addFolderIds'],
    },
  );

export class UpdateUserFolderAccessGroupDto extends createZodDto(
  UpdateUserFolderAccessGroupSchema,
) {}

export type UpdateUserFolderAccessGroupInput = z.infer<
  typeof UpdateUserFolderAccessGroupSchema
>;

export class DeactivateUserGroupDto extends createZodDto(
  DeactivateUserGroupSchema,
) {}
